import {Component, inject} from '@angular/core';
import {ComponentExportConfig, SitRepService} from '../../services/sit-rep.service';
import {ExportComponents} from '../../../../enums/export-components.enum';
import {VulcanIconComponent} from '@eagle-i/vulcan-component-library';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatCheckbox} from '@angular/material/checkbox';
import {FormsModule} from '@angular/forms';
import {MatDialogClose, MatDialogRef} from '@angular/material/dialog';
import {FileDownload} from '../../../../classes/file-download';

interface OptionConfig {
    displayName: string;
    selected: boolean;
    getConfig: () => ComponentExportConfig;
}

@Component({
    selector: 'eaglei-sit-rep-export-modal',
    imports: [VulcanIconComponent, MatIconButton, MatCheckbox, FormsModule, MatButton, MatDialogClose],
    templateUrl: './sit-rep-export-modal.component.html',
    styleUrl: './sit-rep-export-modal.component.scss',
    standalone: true,
})
export class SitRepExportModalComponent {
    private sitRepService = inject(SitRepService);
    private dialogRef = inject(MatDialogRef);

    // TODO: Update this to better pull the config options, this is just a quick approach.
    protected widgetOptions: OptionConfig[] = [
        {
            displayName: 'Customers Impacted Metric',
            selected: false,
            getConfig: () => {
                return {
                    component: ExportComponents.SIT_REP_METRIC,
                    filename: `customer-metric`,
                    width: 400,
                    inputs: {
                        endDate: this.sitRepService.sitRepFilters().endDate.local().toISOString(),
                        states: this.sitRepService.sitRepFilters().locations.map((location) => location.abbreviation),
                        type: 'customer',
                    },
                };
            },
        },
        {
            displayName: 'Max Customers Impacted Metric',
            selected: false,
            getConfig: () => {
                return {
                    component: ExportComponents.SIT_REP_METRIC,
                    filename: `max-customer-metric`,
                    width: 400,
                    inputs: {
                        eventId: this.sitRepService.sitRepFilters().event.id,
                        endDate: this.sitRepService.sitRepFilters().endDate.local().toISOString(),
                        states: this.sitRepService.sitRepFilters().locations.map((location) => location.abbreviation),
                        type: 'max-customer',
                    },
                };
            },
        },
        {
            displayName: 'Utility Impacted Metric',
            selected: false,
            getConfig: () => {
                return {
                    component: ExportComponents.SIT_REP_METRIC,
                    filename: `utility-metric`,
                    width: 400,
                    inputs: {
                        endDate: this.sitRepService.sitRepFilters().endDate.local().toISOString(),
                        states: this.sitRepService.sitRepFilters().locations.map((location) => location.abbreviation),
                        type: 'utility',
                    },
                };
            },
        },
        {
            displayName: 'Max Utility Impacted Metric',
            selected: false,
            getConfig: () => {
                return {
                    component: ExportComponents.SIT_REP_METRIC,
                    filename: `max-utility-metric`,
                    width: 400,
                    inputs: {
                        eventId: this.sitRepService.sitRepFilters().event.id,
                        endDate: this.sitRepService.sitRepFilters().endDate.local().toISOString(),
                        states: this.sitRepService.sitRepFilters().locations.map((location) => location.abbreviation),
                        type: 'max-utility',
                    },
                };
            },
        },
        {
            displayName: 'State Level Outage Map',
            selected: true,
            getConfig: () => {
                return {
                    component: ExportComponents.SIT_REP_MAP,
                    filename: `situation-report-state-map`,
                    width: 500,
                    inputs: {
                        aggregationLevel: 'state',
                        startDate: this.sitRepService.sitRepFilters().startDate.toISOString(),
                        endDate: this.sitRepService.sitRepFilters().endDate.toISOString(),
                        states: this.sitRepService.sitRepFilters().locations.map((location) => location.abbreviation),
                    },
                    delay: 10,
                };
            },
        },
        {
            displayName: 'County Level Outage Map',
            selected: true,
            getConfig: () => {
                return {
                    component: ExportComponents.SIT_REP_MAP,
                    filename: `situation-report-county-map`,
                    width: 500,
                    inputs: {
                        aggregationLevel: 'county',
                        startDate: this.sitRepService.sitRepFilters().startDate.toISOString(),
                        endDate: this.sitRepService.sitRepFilters().endDate.toISOString(),
                        states: this.sitRepService.sitRepFilters().locations.map((location) => location.abbreviation),
                    },
                    delay: 10,
                };
            },
        },
        {
            displayName: '12hr Outage Trend Chart',
            selected: true,
            getConfig: () => {
                return {
                    component: ExportComponents.NOM_CHART,
                    height: 450,
                    inputs: {
                        startDate: this.sitRepService.sitRepFilters().endDate.clone().subtract(12, 'hours').toISOString(),
                        endDate: this.sitRepService.sitRepFilters().endDate.toISOString(),
                        states: this.sitRepService.sitRepFilters().locations.map((location) => location.abbreviation),
                    },
                    delay: 3,
                };
            },
        },
        {
            displayName: 'State Level Outage Table',
            selected: true,
            getConfig: () => {
                return {
                    component: ExportComponents.SIT_REP_TABLE,
                    filename: `situation-report-state-table`,
                    inputs: {
                        startDate: this.sitRepService.sitRepFilters().startDate.toISOString(),
                        endDate: this.sitRepService.sitRepFilters().endDate.toISOString(),
                        states: this.sitRepService.sitRepFilters().locations.map((location) => location.abbreviation),
                        type: 'state',
                        eventId: this.sitRepService.sitRepFilters().event.id,
                    },
                };
            },
        },
        {
            displayName: 'County Level Outage Table',
            selected: true,
            getConfig: () => {
                return {
                    component: ExportComponents.SIT_REP_TABLE,
                    filename: `situation-report-county-table`,
                    inputs: {
                        startDate: this.sitRepService.sitRepFilters().startDate.toISOString(),
                        endDate: this.sitRepService.sitRepFilters().endDate.toISOString(),
                        states: this.sitRepService.sitRepFilters().locations.map((location) => location.abbreviation),
                        type: 'county',
                        eventId: this.sitRepService.sitRepFilters().event.id,
                    },
                };
            },
        },
    ];

    constructor() {}

    public downloadZip(): any {
        const selectedConfigs = this.widgetOptions.filter((option) => option.selected).map((option) => option.getConfig());

        this.sitRepService.exportComponent(selectedConfigs).subscribe({
            next: (blobUrl: string) => {
                if (selectedConfigs.length === 1) {
                    FileDownload.downloadFile(`${selectedConfigs[0].filename || 'situation-report-widget'}.png`, blobUrl);
                } else {
                    FileDownload.downloadFile(`situation-report-widgets.zip`, blobUrl);
                }
                this.dialogRef.close();
            },
            error: (error) => {
                console.error('Unable to export multiple widgets', error);
            },
        });
    }
}
